/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { ReactNode } from 'react'

import { useNavMode } from '@/components/Layout/NavModeContext'

/**
 * Left padding for a blue title bar. With the nav collapsed, its menu button floats over the
 * top left of the page, so the title starts clear of it.
 */
export const useTitleInset = (): string => (useNavMode().isCollapsed ? 'pl-11' : 'pl-4')

/**
 * A page's title in its blue title bar. While the nav is collapsed the title sits next to the
 * floating menu button, so clicking it opens the menu too, a much bigger target than the button.
 */
export const PageTitle: React.FC<{ className?: string; children: ReactNode }> = ({ className = '', children }) => {
  const { isCollapsed, setDrawerOpen } = useNavMode()
  return (
    <h1 className={`text-lg font-bold ${className}`}>
      {isCollapsed ? (
        <button
          type="button"
          className="max-w-full truncate cursor-pointer text-left hover:opacity-80"
          title="Open menu"
          onClick={() => setDrawerOpen(true)}
        >
          {children}
        </button>
      ) : (
        children
      )}
    </h1>
  )
}

/**
 * The full-width title bar an application page (Assets, Trackers, Cold Storage) shows while the
 * nav is collapsed: the app's name at the left and the page's main controls at the right. Only
 * as tall as a small button, plus a little padding.
 */
const AppTitleBar: React.FC<{
  title: string
  /** Where the controls sit: pushed right (default) or straight after the title. */
  align?: 'start' | 'end'
  children?: ReactNode
}> = ({ title, align = 'end', children }) => {
  const inset = useTitleInset()
  return (
    // z-50: above the toolbar row beneath it (z-40), so the dropdowns here open over it.
    <div
      className={`relative z-50 flex items-center justify-between gap-4 min-h-10 py-1 pr-4 ${inset} bg-primary text-primary-content`}
    >
      <PageTitle className="truncate shrink-0">{title}</PageTitle>
      {/* The bar's text is primary-content (white). Controls moved in here keep their white
          triggers and menus (bg-base-100), which would otherwise inherit white-on-white text. */}
      <div
        className={`flex-1 min-w-0 flex ${align === 'start' ? 'justify-start' : 'justify-end'} [&_.bg-base-100]:text-base-content`}
      >
        {children}
      </div>
    </div>
  )
}

export default AppTitleBar
